import { DestroyRef, effect, EffectRef, inject, Injectable, Signal, WritableSignal } from "@angular/core";
import { Menu, MenuContent, MenuContentItem, MenuKeys } from "@models/interfaces/components/Menu.interface";
import { GroupAssignedAmenity } from "@models/interfaces/amenities/AmenityGroupAssigned.interface";
import { MenuContainerService } from "@services/menu/menu-container.service";
import { MenuSelectionService } from "@services/menu/menu-selection.service";
import { MenuFactoryService } from "@services/menu/menu-factory.service";
import { MenuParamsService } from "@services/menu/menu-params.service";
import { DvmService } from "@services/dvm.service";
import { LayoutService } from "@services/layout.service";
import { first } from "rxjs";
import { findMenuContentById } from "@services/menu/menu-content-tree.util";

@Injectable()
export class MenuService {

    // SUB-SERVICES
    private readonly container:  MenuContainerService  = inject(MenuContainerService);
    private readonly selection:  MenuSelectionService   = inject(MenuSelectionService);
    private readonly factory:    MenuFactoryService     = inject(MenuFactoryService);
    private readonly params:     MenuParamsService      = inject(MenuParamsService);
    private readonly dvm:        DvmService             = inject(DvmService);
    private readonly layout:     LayoutService          = inject(LayoutService);
    private readonly destroyRef: DestroyRef             = inject(DestroyRef);

    private initialParamsHandled: boolean = false;
    private restoreTimer: ReturnType<typeof setTimeout> | null = null;

    // EFFECTS
    private readonly onMenusReady: EffectRef = effect((): void => {

        const version: number = this.factory.menusReady();

        if (!version || this.initialParamsHandled) return;

        this.initialParamsHandled = this.restoreInitialSelectionFromParams();

    });

    /**
     * Registra la limpieza del temporizador usado para restaurar seleccion inicial.
     */
    constructor() {
        this.destroyRef.onDestroy((): void => {
            if(this.restoreTimer) clearTimeout(this.restoreTimer);
        });
    }

    // DELEGATED STATE — factory (menus map)
    /**
     * Expone el mapa de menus construido por `MenuFactoryService`.
     */
    public get menus(): Map<MenuKeys, Menu> {

        return this.factory.menus;

    }

    /**
     * Expone la lista reactiva de menus disponible para renderizado.
     */
    public get menusList(): Signal<Menu[]> {

        return this.factory.menusList;

    }

    // DELEGATED STATE — container
    /**
     * Signal que controla la visibilidad del contenedor de menu.
     */
    public get showContainer(): WritableSignal<boolean | null> {

        return this.container.showContainer;

    }

    /**
     * Signal que indica si el contenedor de menu esta animando apertura o cierre.
     */
    public get isAnimating(): WritableSignal<boolean> {

        return this.container.isAnimating;

    }

    /**
     * Duracion de animacion usada por el contenedor de menu.
     */
    public get animation(): number {

        return this.container.animation;

    }

    /**
     * Menu actualmente seleccionado segun la clave activa en el contenedor.
     */
    public get menuSelected(): Menu | undefined {

        const selectedKey: MenuKeys | null = this.container.selectedMenuKey;

        return selectedKey ? this.factory.menus.get(selectedKey) : undefined;

    }

    // DELEGATED STATE — selection
    /**
     * Signal con el item de menu actualmente seleccionado.
     */
    public get itemSelected(): WritableSignal<MenuContentItem | null> {

        return this.selection.itemSelected;

    }

    // DELEGATED METHODS — container

    /**
 * En el breve laberinto de este servicio, `toggleMenuContainer` ordena su pieza del mapa y deja visible su destino.
 *
     * @param key Clave del menu que debe abrirse o cerrarse.
 */
    public toggleMenuContainer(key: MenuKeys): void {

        this.container.toggleMenuContainer(key);

    }

    /**
     * Cierra el contenedor de menu activo.
     */
    public closeMenuContainer(): void {

        this.container.closeMenuContainer();

    }

    // DELEGATED METHODS — selection

    /**
     * Establece un item de menu como seleccion actual y sincroniza parametros de URL.
     *
     * @param menuItem Item de menu que debe quedar seleccionado o `null` para limpiar.
     */
    public setMenuItem(menuItem: MenuContent | null): void {

        this.selection.setMenuItem(menuItem);
        this.updateMenuParams();

    }

    /**
     * Ejecuta la seleccion funcional de un item y actualiza parametros cuando no es vista 3D directa.
     *
     * @param item Item de menu que debe seleccionarse o `null` para limpiar.
     */
    public selectItem(item: MenuContent | null): void {

        this.selection.selectItem(item);

        if (item?.data.is3dView && item.data.viewId) return;

        this.updateMenuParams();

    }

    /**
     * Limpia la seleccion de menu actual sin modificar la apertura del contenedor.
     */
    public clearCurrentSelection(): void {

        this.selection.clearCurrentSelection();

    }

    /**
     * Aplica el estado hover de un item de menu sobre viewer y minimapa.
     *
     * @param menuItem Item sobre el que se esta haciendo hover.
     */
    public hoverMenuItem(menuItem?: MenuContentItem): void {

        this.selection.hoverMenuItem(menuItem);

    }

    /**
     * Limpia el estado hover del item de menu activo.
     */
    public leaveMenuItem(): void {

        this.selection.leaveMenuItem();

    }

    /**
     * Comprueba si un menu contiene el item actualmente seleccionado.
     *
     * @param key Clave del menu a comprobar.
     * @returns `true` si el menu indicado contiene la seleccion actual.
     */
    public hasMenuSelectedItem(key: MenuKeys): boolean {

        return this.selection.hasMenuSelectedItem(this.factory.menus.get(key));

    }

    // DELEGATED METHODS — factory

    /**
     * Inicializa tags de menu en los nodos DVM cuando el mapa ha terminado de cargar.
     */
    public initMenuTags(): void {

        this.factory.initMenuTags();

    }

    /**
     * Convierte una amenity AMS asignada en un item consumible por el menu.
     *
     * @param amenity Amenity asignada a un grupo AMS.
     * @returns Item de menu construido desde la amenity.
     */
    public createMenuContentItem(amenity: GroupAssignedAmenity): MenuContentItem {

        return this.factory.createMenuContentItem(amenity);

    }

    /**
     * Sincroniza la seleccion actual de menu con los parametros de URL.
     */
    private updateMenuParams(): void {

        const selectedKey:  MenuKeys | null             = this.container.selectedMenuKey,
              selectedMenu: Menu | undefined            = selectedKey ? this.factory.menus.get(selectedKey) : undefined,
              selectedItem: MenuContentItem | null      = this.selection.itemSelected();

        this.params.updateMenuParams(selectedKey, selectedItem, selectedMenu);

    }

    /**
     * Restaura la seleccion inicial desde `menu_id` y `menu_item_id` cuando menus y viewer estan listos.
     *
     * @returns `true` si los params ya quedaron gestionados; `false` si debe reintentarse en la siguiente carga de menus.
     */
    private restoreInitialSelectionFromParams(): boolean {

        const {menuId, menuItemId} = this.params.getInitialSelectionParams();

        if (!menuId || !menuItemId) return true;

        const key:          MenuKeys            = menuId as MenuKeys,
              menu:         Menu | undefined    = this.factory.menus.get(key),
              selectedItem: MenuContent | null  = menu ? findMenuContentById(menu.content, menuItemId) ?? null : null;

        if (!menu) return this.factory.menus.size > 0;
        if (!selectedItem) return true;

        const loadSelection = (): void => {

            const duration: number = this.layout.isMobile() ? this.container.animation + 100 : this.container.animation;

            if (!this.layout.isMobile()) this.container.toggleMenuContainer(key);
            else this.container.setMenuSelected(key);

            if(this.restoreTimer) clearTimeout(this.restoreTimer);
            this.restoreTimer = window.setTimeout((): void => {
                this.selectItem(selectedItem);
                this.restoreTimer = null;
            }, duration);

        };

        if (this.dvm.viewer.isLoaded()) {
            loadSelection();
            return true;
        }

        this.dvm.viewer.getObservable('load_success')
            .pipe(first())
            .subscribe((): void => loadSelection());

        return true;

    }

}
